import { useState } from "react";
import { LogOut, KeyRound, User, Menu, Settings } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";
import { useAuth } from "@/hooks/use-auth";
import { useIsMobile } from "@/hooks/use-mobile";
import { Link, useLocation } from "wouter";
import { ChangePasswordDialog } from "@/components/change-password-dialog";
import { COMPANY } from "@/lib/company-info";
import HoferLogo from "@assets/hofer/hofer-logo.png";

function openApiKeySettings() {
  window.dispatchEvent(new CustomEvent("open-api-key-settings"));
}

export function Header() {
  const { user, logout, isLoggingOut } = useAuth();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [location] = useLocation();
  const isMobile = useIsMobile();

  const desktopNavLinks = [
    { href: "/dashboard", label: "Dashboard" },
    { href: "/project/create", label: "Neues Projekt" },
    { href: "/search", label: "Suche" },
    { href: "/completed", label: "Abgeschlossene Angebote" },
    { href: "/knowledge-base", label: "Wissensdatenbank" },
    { href: "/positions-library", label: "Positions-Bibliothek" },
    { href: "/photo-analysis", label: "Foto-Analyse" },
    { href: "/help", label: "Hilfe" },
  ];

  // Auf dem Smartphone bewusst nur die Vor-Ort-relevanten Einträge zeigen.
  const mobileNavLinks = [
    { href: "/dashboard", label: "Meine Projekte" },
    { href: "/project/create", label: "Neues Projekt" },
    { href: "/completed", label: "Abgeschlossene Angebote" },
    { href: "/help", label: "Hilfe" },
  ];

  const navLinks = isMobile ? mobileNavLinks : desktopNavLinks;

  return (
    <header className="bg-white shadow-sm border-b border-gray-200">
      <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center py-3 sm:py-4 gap-3">
          {/*
            Brand-Cluster (Logo + Name + Tagline). Bewusst `shrink-0`,
            damit die Schriften nie unter die Navigation gequetscht werden;
            der Textblock erscheint erst ab `xl`-Breite, sodass auf
            Tablet-/kleinen Desktop-Auflösungen nur das Logo sichtbar ist
            und das Layout nicht überlappt.
          */}
          <Link href="/dashboard" className="flex items-center shrink-0 gap-3">
            <img
              src={HoferLogo}
              alt={COMPANY.brand}
              className="h-8 sm:h-10 w-auto flex-shrink-0"
            />
            <div className="hidden xl:flex flex-col leading-tight whitespace-nowrap">
              {/*
                Markenschriftzug bewusst im Slate-Grauton der
                Hofer-Wortmarke ("Fassade & Betonsanierung"), damit der
                Text mit dem Logo und den darunterliegenden grauen
                Trennlinien harmoniert.
              */}
              <span className="text-sm font-bold text-bauwerk-slate">
                {COMPANY.name}
              </span>
              <span className="text-[11px] text-bauwerk-slate/80">
                {COMPANY.tagline}
              </span>
            </div>
          </Link>

          {/*
            Desktop-Navigation: erst ab `lg` (≥1024px) sichtbar, damit die
            acht Navigations-Einträge plus Benutzer-Button genug Platz
            haben. Darunter wird das Burger-Menü verwendet.
          */}
          <div className="hidden lg:flex items-center gap-3 xl:gap-6">
            <nav className="flex items-center gap-1 xl:gap-3" aria-label="Hauptnavigation">
              {navLinks.map((link) => {
                const isActive = location === link.href;
                return (
                  <Link
                    key={link.href}
                    href={link.href}
                    aria-current={isActive ? "page" : undefined}
                    className={`px-2 xl:px-3 py-1.5 rounded-md text-sm font-medium whitespace-nowrap transition-colors ${
                      isActive
                        ? "bg-bauwerk-teal/10 text-bauwerk-teal"
                        : "text-gray-700 hover:text-bauwerk-teal"
                    }`}
                  >
                    {link.label}
                  </Link>
                );
              })}
            </nav>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="outline"
                  size="sm"
                  className="border-bauwerk-teal text-bauwerk-teal hover:bg-bauwerk-teal hover:text-white transition-colors"
                >
                  <User className="w-4 h-4 mr-2" />
                  {user?.name || "Benutzer"}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-48">
                <DropdownMenuItem onSelect={openApiKeySettings}>
                  <Settings className="w-4 h-4 mr-2" />
                  KI-Einstellungen
                </DropdownMenuItem>
                <ChangePasswordDialog>
                  <DropdownMenuItem onSelect={(e) => e.preventDefault()}>
                    <KeyRound className="w-4 h-4 mr-2" />
                    Passwort ändern
                  </DropdownMenuItem>
                </ChangePasswordDialog>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  onClick={() => logout()}
                  disabled={isLoggingOut}
                  className="text-red-600 focus:text-red-600"
                >
                  <LogOut className="w-4 h-4 mr-2" />
                  Logout
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>

          <div className="lg:hidden">
            <Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
              <SheetTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  aria-label={
                    mobileMenuOpen
                      ? "Navigationsmenü schließen"
                      : "Navigationsmenü öffnen"
                  }
                  aria-expanded={mobileMenuOpen}
                  aria-controls="mobile-navigation"
                  aria-haspopup="dialog"
                >
                  <Menu className="h-6 w-6" aria-hidden="true" />
                </Button>
              </SheetTrigger>
              <SheetContent
                side="right"
                className="w-[280px] sm:w-[320px]"
                id="mobile-navigation"
                aria-label="Mobiles Navigationsmenü"
              >
                <SheetHeader>
                  <SheetTitle className="flex items-center gap-2 text-left">
                    <User className="w-4 h-4" aria-hidden="true" />
                    {user?.name || "Benutzer"}
                  </SheetTitle>
                  <SheetDescription className="sr-only">
                    Hauptnavigation und Kontooptionen
                  </SheetDescription>
                </SheetHeader>
                <nav
                  className="flex flex-col gap-1 mt-6"
                  aria-label="Hauptnavigation"
                >
                  {navLinks.map((link) => {
                    const isActive = location === link.href;
                    return (
                      <Link
                        key={link.href}
                        href={link.href}
                        onClick={() => setMobileMenuOpen(false)}
                        aria-current={isActive ? "page" : undefined}
                        className={`px-3 py-2.5 rounded-md font-medium transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-bauwerk-teal focus-visible:ring-offset-2 ${
                          isActive
                            ? "bg-bauwerk-teal/10 text-bauwerk-teal"
                            : "text-gray-700 hover:bg-gray-100"
                        }`}
                      >
                        {link.label}
                      </Link>
                    );
                  })}
                </nav>
                <div className="border-t border-gray-200 mt-4 pt-4 flex flex-col gap-1">
                  <button
                    type="button"
                    onClick={() => {
                      setMobileMenuOpen(false);
                      openApiKeySettings();
                    }}
                    className="flex items-center gap-2 px-3 py-2.5 rounded-md font-medium text-gray-700 hover:bg-gray-100 transition-colors w-full text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-bauwerk-teal focus-visible:ring-offset-2"
                  >
                    <Settings className="w-4 h-4" aria-hidden="true" />
                    KI-Einstellungen
                  </button>
                  <ChangePasswordDialog>
                    <button
                      type="button"
                      onClick={() => setMobileMenuOpen(false)}
                      className="flex items-center gap-2 px-3 py-2.5 rounded-md font-medium text-gray-700 hover:bg-gray-100 transition-colors w-full text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-bauwerk-teal focus-visible:ring-offset-2"
                    >
                      <KeyRound className="w-4 h-4" aria-hidden="true" />
                      Passwort ändern
                    </button>
                  </ChangePasswordDialog>
                  <button
                    type="button"
                    onClick={() => {
                      logout();
                      setMobileMenuOpen(false);
                    }}
                    disabled={isLoggingOut}
                    className="flex items-center gap-2 px-3 py-2.5 rounded-md font-medium text-red-600 hover:bg-red-50 transition-colors w-full text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-red-600 focus-visible:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <LogOut className="w-4 h-4" aria-hidden="true" />
                    Logout
                  </button>
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </div>
    </header>
  );
}
